import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'

import { Loader, PageFabActions } from 'src/components'
import { DEFAULT_LANGUAGE, PATHS } from 'src/constants'
import { uselAccountFirstRedirect } from 'src/contexts'
import { useGetAccountSimpleId, useGetProductRedirectUrls } from 'src/hooks'

import { EDefaultLandingPage } from 'src/types'
import { isProductHomePath } from 'src/utils/route.utils'

export default function Home() {
  const [, i18n] = useTranslation()
  const { productId, simpleProductId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const [searchParams, setSearchParams] = useSearchParams()
  const allowPreview = searchParams.get('preview')

  const { isRedirected } = uselAccountFirstRedirect()

  const { data, isLoading } = useGetProductRedirectUrls(simpleProductId, i18n.language)

  const { data: accountSimpleId, isLoading: accountSimpleIdIsLoading } = useGetAccountSimpleId(
    undefined,
    simpleProductId,
  )

  const isProductHome = isProductHomePath(location.pathname, simpleProductId)

  useEffect(() => {
    localStorage.removeItem('preview')

    if (allowPreview === 'true') {
      localStorage.setItem('preview', 'true')
      searchParams.delete('preview')
      setSearchParams(searchParams)
    }
    return () => {
      localStorage.removeItem('preview')
    }
  }, [])

  useEffect(() => {
    if (!isLoading && data) {
      if (data.override_url) {
        window.location.replace(data.override_url)
        return
      }

      const allowPreview = localStorage.getItem('preview') === 'true'

      if (!data.public && !allowPreview && !accountSimpleIdIsLoading && accountSimpleId) {
        // Redirect if product is not public
        navigate(
          i18n.language === DEFAULT_LANGUAGE
            ? `/${PATHS.ACCOUNT}/${accountSimpleId}`
            : `/${i18n.language}/${PATHS.ACCOUNT}/${accountSimpleId}`,
        )
        return
      }

      //  Default landing redirect — now ONLY when visiting the product home QR URL
      if (
        isProductHome &&
        data.default_landing_page &&
        !data.override_url &&
        (data.public || allowPreview)
      ) {
        if (data.default_landing_page === EDefaultLandingPage.IMPACT) {
          navigate(
            i18n.language === DEFAULT_LANGUAGE
              ? `/${simpleProductId}/${PATHS.IMPACTS}`
              : `/${i18n.language}/${simpleProductId}/${PATHS.IMPACTS}`,
          )
          return
        }
        if (data.default_landing_page === EDefaultLandingPage.JOURNEY) {
          navigate(
            i18n.language === DEFAULT_LANGUAGE
              ? `/${simpleProductId}/${PATHS.JOURNEY}`
              : `/${i18n.language}/${simpleProductId}/${PATHS.JOURNEY}`,
          )
        }
        if (
          !isRedirected &&
          !accountSimpleIdIsLoading &&
          data.default_landing_page === EDefaultLandingPage.ACCOUNT
        ) {
          navigate(
            i18n.language === DEFAULT_LANGUAGE
              ? `/${PATHS.ACCOUNT}/${accountSimpleId}`
              : `/${i18n.language}/${PATHS.ACCOUNT}/${accountSimpleId}`,
          )
        }
        return
      }
    }
  }, [isLoading, data, accountSimpleId, accountSimpleIdIsLoading])

  if (isLoading || accountSimpleIdIsLoading) {
    return <Loader />
  }

  return (
    <>
      <Outlet />
      {(productId || simpleProductId) && <PageFabActions />}
    </>
  )
}
